import React from 'react';
import styled from '@emotion/styled';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloseIcon from '@mui/icons-material/Close';
import { PICKER_COLOR } from '@/components/common/datePicker/pickerTokens';

/** 폰 시트·PC 팝업 안 공용 모양(시안 calmock m3 / pc) */

/** 폰 시트 맨 위 요약 줄 — "10월 12일 (일) · 오후 2:00" */
export const SummaryLine = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 6px;
  min-height: 46px;
  margin-bottom: 8px;
  padding: 10px 14px;
  border: 2px solid ${PICKER_COLOR.primary};
  border-radius: 12px;
  background: ${PICKER_COLOR.primaryCard};
  font-size: 15px;
  color: ${PICKER_COLOR.primaryDeep};
  box-sizing: border-box;

  b {
    font-weight: 800;
  }

  em {
    font-style: normal;
    font-weight: 600;
    color: ${PICKER_COLOR.muted};
  }
`;

/** "시간" · "시작 시간" 같은 작은 제목 줄(오른쪽 안내 선택) */
export const SectionLabel = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin: 12px 2px 8px;
  font-size: 13px;
  font-weight: 700;
  color: ${PICKER_COLOR.sub};

  span {
    font-weight: 500;
    color: ${PICKER_COLOR.muted};
  }
`;

/** PC 팝업 — 왼쪽 달력, 오른쪽 시간 칸(폭 180px, 2열 버튼) */
export const PcGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 180px;
  gap: 18px;
  align-items: start;

  /*
   * 작은 창 안에서 창 안쪽 폭으로 줄어든 팝업(FilterPanel 이 붙이는 data-popover-narrow, 2026-10-07 면접 요청 창 460) —
   * 시간 칸 140px, 달력 칸은 PickerCalendar 가 촘촘하게. 시간 칸은 달력 높이만큼(고정 350 이면 창 아래 버튼까지 내려온다)
   */
  [data-popover-narrow] & {
    grid-template-columns: minmax(0, 1fr) 140px;
    gap: 12px;
    align-items: stretch;
  }
`;

/** PC 시간 칸 — 세그먼트·버튼(세로 스크롤)·안내. 달력 높이에 맞춘다 */
export const PcTimeColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  height: 350px;

  /* 휠·[선택]·안내는 줄어들지 않고, 버튼 목록만 남는 높이에서 스크롤 */
  > * {
    flex-shrink: 0;
  }

  > .pc-time-scroll {
    flex-shrink: 1;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 2px 4px 2px 0;
  }

  /* 좁은 팝업 — 높이는 달력 줄에 맞추고(버튼 목록은 높이 0 에서 남는 만큼 늘어 스크롤) */
  [data-popover-narrow] & {
    height: auto;
  }

  [data-popover-narrow] & > .pc-time-scroll {
    flex: 1 1 0;
    height: 0;
  }
`;

/** PC 휠 아래 [선택] 버튼 */
export const PcSelectButton = styled.button`
  flex-shrink: 0;
  height: 40px;
  margin-top: 8px;
  border: none;
  border-radius: 12px;
  background: ${PICKER_COLOR.primary};
  color: #fff;
  font-family: inherit;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    background: ${PICKER_COLOR.primaryDisabled};
    cursor: default;
  }
`;

export const PickerError = styled.p`
  margin: 8px 2px 0;
  font-size: 13px;
  color: ${PICKER_COLOR.danger};
  white-space: pre-line;
  word-break: keep-all;
`;

export const PickerNote = styled.p`
  margin: 6px 2px 0;
  font-size: 12.5px;
  color: ${PICKER_COLOR.muted};
  word-break: keep-all;
`;

/** 시트/팝업 안 내용 틀 — 휠·가로 전환이 있어 스와이프 뒤로가기와 겹치지 않게(data-no-swipe-back) */
export const PickerBody = styled.div`
  padding-top: 8px;
  overflow-x: hidden;
`;

// ───────── 폰 시트 단계(날짜 → 시간) ─────────

/** 단계 틀 — 넘어갈 때 가로로 밀려 들어온다(앞으로: 오른쪽에서, 뒤로: 왼쪽에서) */
export const StepPane = styled.div<{ direction: 'forward' | 'back' }>`
  animation: ${({ direction }) => (direction === 'forward' ? 'pickerStepFromRight' : 'pickerStepFromLeft')} 200ms ease-out;

  @keyframes pickerStepFromRight {
    from { transform: translateX(40px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @keyframes pickerStepFromLeft {
    from { transform: translateX(-40px); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;


// ───────── 폰 시트 단계 탭 [날짜 | 시간] (시안 ui-tB) ─────────

const TabRow = styled.div`
  display: flex;
  margin: 0 0 14px;
  border-bottom: 1px solid ${PICKER_COLOR.line};
`;

const TabButton = styled.button<{ selected: boolean }>`
  flex: 1;
  min-width: 0;
  padding: 8px 4px 11px;
  border: none;
  background: none;
  font-family: inherit;
  font-size: 15px;
  font-weight: ${({ selected }) => (selected ? 800 : 600)};
  color: ${({ selected }) => (selected ? PICKER_COLOR.text : PICKER_COLOR.muted)};
  box-shadow: ${({ selected }) => (selected ? `inset 0 -2px 0 ${PICKER_COLOR.primary}` : 'none')};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  small {
    display: block;
    margin-bottom: 2px;
    font-size: 12px;
    font-weight: 500;
    color: ${PICKER_COLOR.muted};
  }

  &:disabled {
    color: ${PICKER_COLOR.placeholder};
    cursor: default;
  }
`;

export interface StepTab {
  key: string;
  /** 위 작은 라벨(날짜·시간·시작·종료) */
  label: string;
  /** 고른 값(없으면 "고르기 전") */
  value: string | null;
  disabled?: boolean;
}

/** 단계 탭 — 지금 단계에 청록 밑줄, 누르면 그 단계로(뒤로가기는 시트를 닫는다) */
export const StepTabs: React.FC<{ tabs: StepTab[]; current: string; onSelect: (key: string) => void; label: string }> = ({ tabs, current, onSelect, label }) => (
  <TabRow role="tablist" aria-label={label}>
    {tabs.map((tab: StepTab) => (
      <TabButton
        key={tab.key}
        type="button"
        role="tab"
        aria-selected={tab.key === current}
        selected={tab.key === current}
        disabled={tab.disabled}
        onClick={() => onSelect(tab.key)}
      >
        <small>{tab.label}</small>
        {tab.value ?? '고르기 전'}
      </TabButton>
    ))}
  </TabRow>
);

/** 달력 아래 작은 안내("날짜를 누르면 시간으로 넘어가요") */
export const StepHint = styled.p`
  margin: 10px 2px 0;
  font-size: 13.5px;
  color: ${PICKER_COLOR.muted};
`;

// ───────── 입력 칸(누르면 시트/팝업) ─────────

const TriggerButton = styled.button<{ hasError: boolean; active: boolean }>`
  width: 100%;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border: 1px solid ${({ hasError, active }) => (hasError ? PICKER_COLOR.danger : active ? PICKER_COLOR.primary : PICKER_COLOR.border)};
  box-shadow: ${({ active, hasError }) => (active && !hasError ? `inset 0 0 0 1px ${PICKER_COLOR.primary}` : 'none')};
  border-radius: 12px;
  background: #fff;
  box-sizing: border-box;
  min-width: 0;
  font-family: inherit;
  font-size: 15px;
  color: ${PICKER_COLOR.text};
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  svg {
    flex: 0 0 auto;
    font-size: 20px;
    color: ${PICKER_COLOR.primary};
  }

  > span {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  > span.placeholder {
    font-size: 14px;
    color: ${PICKER_COLOR.placeholder};
  }

  &:focus-visible {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? PICKER_COLOR.danger : PICKER_COLOR.primary)};
  }

  &:disabled {
    background-color: #f3f5f4;
    color: ${PICKER_COLOR.muted};
    cursor: default;
  }

  /* 폰 두 칸 한 줄(약 170px)에 "10/12(일) 오후 2:00" 이 들어가게 */
  @media (max-width: 768px) {
    gap: 6px;
    padding: 0 10px;
    font-size: 14px;

    svg {
      font-size: 18px;
    }
  }
`;

export interface PickerTriggerProps {
  icon: 'calendar' | 'clock';
  /** 고른 값 표시(없으면 placeholder) */
  text: string;
  placeholder: string;
  /** 스크린리더 이름(칸 이름) */
  label: string;
  hasError: boolean;
  open: boolean;
  onClick: () => void;
}

/** 52px 상자형 입력 칸 — 값 "10/12(일) 오후 2:00". 잠긴 fieldset 안이면 눌러도 안 열린다(버튼 disabled) */
export const PickerTrigger = React.forwardRef<HTMLButtonElement, PickerTriggerProps>(
  ({ icon, text, placeholder, label, hasError, open, onClick }, ref) => (
    <TriggerButton
      ref={ref}
      type="button"
      hasError={hasError}
      active={open}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-invalid={hasError}
      aria-label={text ? `${label}, ${text}` : label}
      onClick={onClick}
    >
      {icon === 'clock' ? <AccessTimeIcon aria-hidden /> : <CalendarTodayOutlinedIcon aria-hidden />}
      {text ? <span>{text}</span> : <span className="placeholder">{placeholder}</span>}
    </TriggerButton>
  ),
);
PickerTrigger.displayName = 'PickerTrigger';

/** 여러 날짜 선택에서 쓰는 날짜 칩과 추가 버튼. */
export const DateChip = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-height: 36px;
  /* 끝 빼기 아이콘 쪽만 4px 줄임(UI 가이드 4.1) */
  padding: 0 8px 0 12px;
  border: 1px solid ${PICKER_COLOR.primary};
  border-radius: 18px;
  background: ${PICKER_COLOR.primarySoft};
  color: ${PICKER_COLOR.primaryDeep};
  font: inherit;
  font-size: 14px;
  cursor: pointer;
`;

/** 고른 날 칩 끝 빼기 표시 — 글자 "×" 대신 SVG(UI 가이드 4.1, 역 칩 StationSearchField 와 같은 14px) */
export const DateChipRemove = styled(CloseIcon)`
  && {
    display: block;
    width: 14px;
    height: 14px;
  }
`;

/**
 * 날짜를 더 고르는 버튼(＋ 날짜 고르기 / ＋ 날짜 추가) — 아직 고르지 않은 칩 모양(흰 바탕·회색 테두리).
 * 고른 날([DateChip], 청록)과 같은 색이면 아무것도 안 골랐는데 체크된 것처럼 보인다(2026-10-01).
 */
/** [날짜 추가] — 다른 알약 칩과 같은 36px, 앞 + 는 SVG(UI 가이드 4.1, 아이콘 쪽 안쪽 여백 4px 줄임) */
export const DateAddChip = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 36px;
  padding: 0 14px 0 10px;

  svg {
    display: block;
    width: 18px;
    height: 18px;
  }

  border: 1px solid ${PICKER_COLOR.border};
  border-radius: 19px;
  background: #fff;
  color: ${PICKER_COLOR.text};
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &[aria-invalid='true'] {
    border-color: ${PICKER_COLOR.danger};
  }
`;
